/*
 * Parsing and formatting for the coordinates people copy off plats: decimal
 * degrees, degrees-minutes-seconds and degrees-decimal-minutes, with or
 * without hemisphere letters, in either order.
 */

import { DEFAULT_SPC_ZONE, SPC_ZONES } from "./statePlane";

export const DATUMS = {
  WGS84: "WGS84",
  NAD83: "NAD83",
  NAD27: "NAD27",
};

export const COORD_FORMATS = {
  DD: "DD",
  DMS: "DMS",
  SPC: "SPC", // State Plane easting/northing, in US survey feet
};

export const DEFAULT_COORDINATE_SYSTEM = {
  datum: DATUMS.WGS84,
  format: COORD_FORMATS.DD,
  zone: DEFAULT_SPC_ZONE,
};

const STORAGE_KEY = "mapCoordinateSystem";

/* Persisted because a user works one basin at a time — re-picking NAD27 on
 * every page load is how a plat coordinate eventually gets entered as WGS84. */
export const loadCoordinateSystem = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY));

    if (!stored) return DEFAULT_COORDINATE_SYSTEM;

    const known = SPC_ZONES.some((zone) => zone.key === stored.zone);

    return {
      datum: DATUMS[stored.datum] || DEFAULT_COORDINATE_SYSTEM.datum,
      format: COORD_FORMATS[stored.format] || DEFAULT_COORDINATE_SYSTEM.format,
      zone: known ? stored.zone : DEFAULT_COORDINATE_SYSTEM.zone,
    };
  } catch (_err) {
    return DEFAULT_COORDINATE_SYSTEM;
  }
};

export const saveCoordinateSystem = (value) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (_err) {
    // Private browsing or a full quota — the selection just won't persist.
  }
};

export const DATUM_OPTIONS = [
  { value: DATUMS.WGS84, label: "WGS84" },
  { value: DATUMS.NAD83, label: "NAD83" },
  { value: DATUMS.NAD27, label: "NAD27" },
];

export const FORMAT_OPTIONS = [
  { value: COORD_FORMATS.DD, label: "DD" },
  { value: COORD_FORMATS.DMS, label: "DMS" },
  { value: COORD_FORMATS.SPC, label: "SPC" },
];

// State Plane is defined for NAD27 and NAD83 only — there is no WGS84 grid.
export const isSpcCompatible = (datum) => datum !== DATUMS.WGS84;

// Working area (TX, NM, LA) with a wide margin. Used to catch the two mistakes
// that account for nearly every bad paste: swapped lat/lng and a longitude
// that lost its minus sign.
const REGION_BOUNDS = { minLng: -110, maxLng: -88, minLat: 25, maxLat: 38 };

const DECIMALS_DD = 6; // ~0.1 m — finer than a plat states its coordinates
const DECIMALS_SECONDS = 2;

const LAT_LABEL_RE = /\blat(itude)?\s*\.?\s*[:=]?/gi;
const LNG_LABEL_RE = /\b(?:lng|long?(itude)?)\s*\.?\s*[:=]?/gi;

const normalize = (input) =>
  String(input)
    .replace(/[°º]/g, "°")
    .replace(/[′‘’`]/g, "'")
    .replace(/[″“”]/g, '"')
    .replace(/[‐-―−]/g, "-")
    // Plats label their columns; parseCoordinate reads the label order
    // before the labels are stripped here.
    .replace(LAT_LABEL_RE, " ")
    .replace(LNG_LABEL_RE, " ")
    .trim();

/* Numbers, standalone hemisphere letters and separators. A letter only counts
 * when it is not part of a word, so footage calls (FNEL, FSWL) and headings
 * do not read as hemispheres. */
const TOKEN_RE = /(-?\d+(?:\.\d+)?)|((?<![A-Za-z])[NSEW](?![A-Za-z]))|([,;])/gi;

const toDecimal = ([degrees, minutes = 0, seconds = 0]) =>
  Math.abs(degrees) + minutes / 60 + seconds / 3600;

const isValidComponent = ({ numbers }) => {
  if (!numbers.length) return false;

  const [, minutes, seconds] = numbers;

  if (minutes !== undefined && minutes >= 60) return false;
  if (seconds !== undefined && seconds >= 60) return false;

  return true;
};

const finishComponent = (component) => {
  const value = toDecimal(component.numbers);
  const hemisphere = component.hemisphere;

  const negative = hemisphere
    ? hemisphere === "S" || hemisphere === "W"
    : component.numbers[0] < 0;

  return {
    value: negative ? -value : value,
    isLat: hemisphere === "N" || hemisphere === "S",
    isLng: hemisphere === "E" || hemisphere === "W",
  };
};

/* Walks the tokens left to right, grouping them into two components. A number
 * joins the open component as degrees/minutes/seconds; it starts a new one
 * when the open component cannot take it — a fractional degree has no minutes
 * after it, fractional minutes have no seconds, and nothing has a fourth
 * number. A hemisphere letter closes the open component, or waits to lead the
 * next one when nothing is open. */
const splitComponents = (text) => {
  const components = [];
  let open = null;
  let pendingHemisphere = null;

  const close = () => {
    if (open) components.push(open);
    open = null;
  };

  TOKEN_RE.lastIndex = 0;
  let token;

  while ((token = TOKEN_RE.exec(text)) !== null) {
    const [, number, letter, separator] = token;

    if (separator) {
      close();
      continue;
    }

    if (letter) {
      // A component that already carries a hemisphere cannot take a trailing
      // one — the letter belongs to whatever comes next ("N 31 W 103").
      if (open && open.numbers.length && !open.hemisphere) {
        open.hemisphere = letter.toUpperCase();
        close();
      } else {
        pendingHemisphere = letter.toUpperCase();
      }
      continue;
    }

    const value = parseFloat(number);
    const filled = open ? open.numbers.length : 0;
    const last = filled ? open.numbers[filled - 1] : null;
    const lastIsFractional = last !== null && !Number.isInteger(last);

    if (!open || filled >= 3 || lastIsFractional || pendingHemisphere) {
      close();
      open = { numbers: [value], hemisphere: pendingHemisphere };
      pendingHemisphere = null;
      continue;
    }

    open.numbers.push(Math.abs(value));
  }

  close();

  return components;
};

/* Parses a single "lat, lng" pair. Returns null when the text is not a
 * coordinate — callers use that to keep a row in an editing state rather than
 * rejecting the keystroke. */
export const parseCoordinate = (input) => {
  if (input === null || input === undefined) return null;

  const raw = String(input);

  /* Which label comes first is the only ordering signal a labelled pair
   * carries — "Lng: -88.9 | Lat: 30.1" has no hemisphere letters and both
   * values fit either axis, so without this the pair would read lat-first. */
  const latIndex = raw.search(LAT_LABEL_RE);
  const lngIndex = raw.search(LNG_LABEL_RE);
  const lngLabelledFirst =
    latIndex >= 0 && lngIndex >= 0 && lngIndex < latIndex;

  const text = normalize(raw);
  if (!text) return null;

  const parts = splitComponents(text);

  if (parts.length !== 2 || !parts.every(isValidComponent)) return null;

  const [first, second] = parts.map(finishComponent);

  // Hemisphere letters outrank the labels — they sit on the value itself.
  const lngFirst =
    first.isLng ||
    second.isLat ||
    (lngLabelledFirst && !first.isLat && !second.isLng);

  let lat;
  let lng;

  if (lngFirst) {
    lat = second.value;
    lng = first.value;
  } else {
    lat = first.value;
    lng = second.value;
  }

  // Unlabelled pairs are assumed lat-first; a latitude past the poles means
  // they were written the other way round.
  if (Math.abs(lat) > 90 && Math.abs(lng) <= 90) {
    [lat, lng] = [lng, lat];
  }

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;

  return { lat, lng };
};

/* Splits pasted text into one coordinate per line, ignoring lines that do not
 * parse — plats are pasted with headings and footage calls mixed in. */
export const parseCoordinateList = (input) => {
  if (!input) return [];

  return String(input)
    .split(/[\r\n;]+/)
    .map((line) => parseCoordinate(line))
    .filter(Boolean);
};

export const isWithinRegion = ({ lat, lng } = {}) =>
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  lat >= REGION_BOUNDS.minLat &&
  lat <= REGION_BOUNDS.maxLat &&
  lng >= REGION_BOUNDS.minLng &&
  lng <= REGION_BOUNDS.maxLng;

/* Why a coordinate looks wrong, or null when it looks fine. Swapping and a
 * dropped minus are worth naming explicitly because the fix is obvious once
 * the user is told which one it is. */
export const regionWarning = (coordinate) => {
  if (!coordinate) return null;
  if (isWithinRegion(coordinate)) return null;

  const { lat, lng } = coordinate;

  if (isWithinRegion({ lat: lng, lng: lat })) return "Lat and lng look swapped";
  if (isWithinRegion({ lat, lng: -lng })) return "Longitude should be negative";

  return "Outside TX / NM / LA";
};

const formatDms = (value, positive, negative) => {
  const hemisphere = value < 0 ? negative : positive;
  const absolute = Math.abs(value);

  let degrees = Math.floor(absolute);
  const minutesFloat = (absolute - degrees) * 60;
  let minutes = Math.floor(minutesFloat);
  let seconds = Number(
    ((minutesFloat - minutes) * 60).toFixed(DECIMALS_SECONDS)
  );

  if (seconds >= 60) {
    seconds = 0;
    minutes += 1;
  }

  if (minutes >= 60) {
    minutes = 0;
    degrees += 1;
  }

  return `${degrees}°${String(minutes).padStart(2, "0")}'${seconds
    .toFixed(DECIMALS_SECONDS)
    .padStart(5, "0")}"${hemisphere}`;
};

export const formatCoordinate = (
  { lat, lng } = {},
  format = COORD_FORMATS.DD
) => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "";

  if (format === COORD_FORMATS.DMS) {
    return `${formatDms(lat, "N", "S")} ${formatDms(lng, "E", "W")}`;
  }

  return `${lat.toFixed(DECIMALS_DD)}, ${lng.toFixed(DECIMALS_DD)}`;
};

/* Numbers with thousands separators, the way plats print eastings and
 * northings. The greedy first branch keeps "1,449,095.45" whole instead of
 * reading the separators as a pair delimiter. */
const NUMBER_RE = /-?\d{1,3}(?:,\d{3})+(?:\.\d+)?|-?\d+(?:\.\d+)?/g;

const toNumber = (text) => parseFloat(String(text).replace(/,/g, ""));

export const formatFeet = (value) =>
  value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const formatStatePlane = ({ x, y } = {}) => {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return "";

  return `X ${formatFeet(x)}  Y ${formatFeet(y)}`;
};

/* Accepts what a plat states — "X = 1,449,095.45  Y = 625,444.54" in either
 * order — and a bare pair, which is read as easting first. */
export const parseStatePlane = (input) => {
  if (input === null || input === undefined) return null;

  const text = String(input);

  const labelled = /(?:^|[^A-Za-z])([XY])\s*[:=]?\s*(-?[\d,]+(?:\.\d+)?)/gi;
  const found = {};

  let match;
  while ((match = labelled.exec(text)) !== null) {
    found[match[1].toUpperCase()] = toNumber(match[2]);
  }

  if (Number.isFinite(found.X) && Number.isFinite(found.Y)) {
    return { x: found.X, y: found.Y };
  }

  NUMBER_RE.lastIndex = 0;
  const numbers = text.match(NUMBER_RE) || [];

  if (numbers.length !== 2) return null;

  const x = toNumber(numbers[0]);
  const y = toNumber(numbers[1]);

  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

  return { x, y };
};

export const formatLat = (lat, format = COORD_FORMATS.DD) => {
  if (!Number.isFinite(lat)) return "";

  return format === COORD_FORMATS.DMS
    ? formatDms(lat, "N", "S")
    : lat.toFixed(DECIMALS_DD);
};

export const formatLng = (lng, format = COORD_FORMATS.DD) => {
  if (!Number.isFinite(lng)) return "";

  return format === COORD_FORMATS.DMS
    ? formatDms(lng, "E", "W")
    : lng.toFixed(DECIMALS_DD);
};

export const isSpcSystem = ({ datum, format } = {}) =>
  format === COORD_FORMATS.SPC && isSpcCompatible(datum);

/* Parses typed text in whichever system is selected. Returns {lat, lng} for
 * the geographic formats and {x, y} for State Plane — callers branch on the
 * same `isSpcSystem` check they used to pick the format. */
export const parseInput = (text, system) =>
  isSpcSystem(system) ? parseStatePlane(text) : parseCoordinate(text);
